import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import { CopilotAction } from 'models/copilot/copilotAction';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { CopilotPanelContextDefinition, CopilotPanelDefinitionMap } from 'models/copilot/copilotPanelContextDefinition';
import { CopilotAnnualPlanPanelContextDefinition } from '../../AnnualPlan/copilotAnnualPlanPanelContextDefinition';
import { CopilotAuditPreparationPanelContextDefinition } from '../../AuditPreparation/copilotAuditPreparationPanelContextDefinition';
import { CopilotControlDocumentationAuditPanelContextDefinition } from '../../ControlDocumentationAudit/copilotControlDocumentationAuditPanelContextDefinition';
import { CopilotControlEvidenceAuditPanelContextDefinition } from '../../ControlEvidenceAudit/copilotControlEvidenceAuditPanelContextDefinition';
import { CopilotFrameworkAuditPanelContextDefinition } from '../../FrameworkAudit/copilotFrameworkAuditPanelContextDefinition';
import { CopilotLibraryPanelContextDefinition } from '../../Library/copilotLibraryPanelContextDefinition';
import { CopilotProcessesPanelContextDefinition } from '../../Processes/copilotProcessesPanelContextDefinition';
import { CopilotProjectsPanelContextDefinition } from '../../Projects/copilotProjectsPanelContextDefinition';
import { CopilotTaskEvidenceAuditPanelContextDefinition } from '../../TaskEvidenceAudit/copilotTaskEvidenceAuditPanelContextDefinition';
import { CopilotTrialWelcomePanelContextDefinition } from '../../TrialWelcome/copilotTrialWelcomePanelContextDefinition';
import { CopilotGenericPanelContextDefinition } from '../../copilotGenericPanelContextDefinition';

export function getCopilotPanelContextDefinitions(): CopilotPanelContextDefinition[] {
  const definitions: CopilotPanelContextDefinition[] = [];

  definitions.push(new CopilotGenericPanelContextDefinition());
  definitions.push(new CopilotAnnualPlanPanelContextDefinition());
  definitions.push(new CopilotLibraryPanelContextDefinition());
  definitions.push(new CopilotProcessesPanelContextDefinition());
  definitions.push(new CopilotControlDocumentationAuditPanelContextDefinition());
  definitions.push(new CopilotControlEvidenceAuditPanelContextDefinition());
  definitions.push(new CopilotTaskEvidenceAuditPanelContextDefinition());
  definitions.push(new CopilotFrameworkAuditPanelContextDefinition());
  definitions.push(new CopilotAuditPreparationPanelContextDefinition());
  definitions.push(new CopilotProjectsPanelContextDefinition());
  definitions.push(new CopilotTrialWelcomePanelContextDefinition());

  return definitions;
}

export function getCopilotPanelContextDefinitionMap(): CopilotPanelDefinitionMap {
  const map: CopilotPanelDefinitionMap = new Map();
  getCopilotPanelContextDefinitions().forEach((definition) => map.set(definition.context, definition));

  return map;
}

/**
 * The definition that runs a process: the active usage's when it owns the process (or no process is named), else the
 * Generic usage's when that owns it (a process a card or a typed turn starts on a scene of another usage), else the
 * active usage's. The action listing and the scene's own reads stay on the active definition.
 */
export function resolveProcessDefinition(
  definitions: CopilotPanelDefinitionMap,
  activeDefinition: CopilotPanelContextDefinition | undefined,
  processId: TenantProcess | undefined,
  content: CopilotPanelContentBase | undefined,
): CopilotPanelContextDefinition | undefined {
  if (processId === undefined || !activeDefinition || activeDefinition.ownsProcess(processId, content)) {
    return activeDefinition;
  }

  const genericDefinition = definitions.get(CopilotPanelContext.Generic);

  return genericDefinition?.ownsProcess(processId, content) ? genericDefinition : activeDefinition;
}

// The process running in an action list: the one that holds the panel first, else one in the background
export const getRunningProcessId = (actions: CopilotAction[]): TenantProcess | undefined => {
  const running = actions.filter((action) => action.status === TenantProcessStatus.InProgress && action.processId);

  return (running.find((action) => action.runsInBackground !== true) ?? running[0])?.processId;
};

export function getCopilotPanelContextDefinition(
  context: CopilotPanelContext,
): CopilotPanelContextDefinition | undefined {
  return getCopilotPanelContextDefinitionMap().get(context);
}
